
import * as echarts from 'echarts'
import { defineComponent, onBeforeUnmount, onMounted, markRaw, ref, computed, watchEffect, nextTick } from 'vue'
import { getHMS, getThisMonthStartTime, getThisWeekStartTime, getThisYearStartTime, getTodayNowTime, getTodayStartTime, getYMD } from '@/utils/date.util'
import { ThemeModule } from '@/store/modules/theme.module'
import { ThemeType } from '@/assets/data/theme'
import OpenTicketReportApi from '@/api/OpenTicketReportApi'

export default defineComponent({
  setup () {
    let timer: number | undefined
    const myEcharts = ref<echarts.ECharts>()
    const option = {
      tooltip: {
        trigger: 'axis',
        borderWidth: 0,
        axisPointer: {
          type: 'cross'
        }
      },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitNumber: 4,
        axisPointer: {
          label: {
            formatter: function (x) {
              return getShowTime(x.value)
            }
          }
        }
      },
      yAxis: {
        type: 'value',
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        },
        minInterval: 1
      },
      grid: {
        left: 0,
        right: 10,
        top: 20,
        bottom: 0,
        containLabel: true
      },
      series: [
        { 
          type: 'line',
          smooth: true,
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(54, 114, 210, 0.39)' }, 
                { offset: 1, color: 'rgba(54, 114, 210, 0)' }
              ]
            }
          },
          lineStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#52C5FF' }, // 0% 处的颜色
                { offset: 1, color: '#0E42D2' } // 100% 处的颜色
              ]
            }
          }
        }
      ]
    }
    const timeRef = ref<string>('THIS_DAY')
    const timeMap = new Map([
      ['THIS_DAY', '今日'],
      ['THIS_WEEK', '本周'],
      ['THIS_MONTH', '本月'],
      ['THIS_YEAR', '本年']
    ])
    const timeFilter = computed(() => {
      return (value) => {
        return timeMap.get(value + '')
      }
    })
    const themeColors = computed(() => ThemeModule.themeColors) 
    const theme = computed(() => ThemeModule.themeType)

    function getShowTime (date) {
      if (timeRef.value === 'THIS_DAY') {
        return getHMS(date)
      } else {
        return getYMD(date)
      }
    }

    watchEffect(() => {
      if (theme.value === ThemeType.THEME_DARK) {
        myEcharts.value && myEcharts.value.setOption({
          tooltip: {
            extraCssText: 'background: linear-gradient(304.17deg, rgba(36, 176, 255, 0.6) -6.04%, rgba(9, 67, 132, 0.6) 85.2%); border-radius: 6px',
            formatter: (params) => {
              return `
                <div class="fs12 color-08">${getShowTime(params[0].data[0])}</div>
                <div class="fs12 color-08" style="
                  min-width: 164px;
                  margin-top: 4px;
                  width: 100%;
                  height: 32px;
                  line-height:32px;
                  background: linear-gradient(90deg, #0E42D2 0%, #52C5FF 101.59%);
                  border-radius: 4px;
                  padding: 0 8px
                ">
                  <span>工单量</span>
                  <span style="float: right">${params[0].data[1] || '-'}</span>
                </div>
              `
            }
          },
          yAxis: {
            splitLine: {
              lineStyle: {
                color: themeColors.value['border-color']
              }
            }
          }
        })
      } else if (theme.value === ThemeType.THEME_BRIGHT) {
        myEcharts.value && myEcharts.value.setOption({
          tooltip: {
            extraCssText: 'background: linear-gradient(300.16deg, rgba(47, 129, 232, 0.6) 2.92%, rgba(78, 189, 253, 0.6) 84.87%); border-radius: 6px',
            formatter: (params) => {
              return `
                <div class="fs12 color-06">${getShowTime(params[0].data[0])}</div>
                <div class="fs12 color-08" style="
                  min-width: 164px;
                  margin-top: 4px;
                  width: 100%;
                  height: 32px;
                  line-height:32px;
                  background: rgba(255, 255, 255, 0.8);
                  box-shadow: 6px 0px 20px rgba(34, 87, 188, 0.1);
                  border-radius: 4px;
                  padding: 0 8px
                ">
                  <span>工单量</span>
                  <span style="float: right">${params[0].data[1] || '-'}</span>
                </div>
              `
            }
          },
          yAxis: {
            splitLine: {
              lineStyle: {
                color: themeColors.value['border-color']
              }
            }
          }
        })
      }
    })

    function setTime (status: 'THIS_DAY' | 'THIS_WEEK' | 'THIS_MONTH' | 'THIS_YEAR') {
      timeRef.value = status
      clearTimeout(timer)
      chatRequestCountReport()
    }

    function renderChart () {
      const chartDom = document.getElementById('ticket-trend') as HTMLElement;
      myEcharts.value = markRaw(echarts.init(chartDom))
      myEcharts.value.setOption(option)
      chartDom.removeAttribute("_echarts_instance_")
    }

    function chatRequestCountReport () {
      let dateType = 'day'
      let startDate: string | null = null
      const endDate: string | null = getTodayNowTime()
      if (timeRef.value === 'THIS_DAY') {
        dateType = 'hour'
        startDate = getTodayStartTime()
      } else if (timeRef.value === 'THIS_WEEK') {
        startDate = getThisWeekStartTime()
      } else if (timeRef.value === 'THIS_MONTH') {
        startDate = getThisMonthStartTime()
      } else if (timeRef.value === 'THIS_YEAR') {
        dateType = 'month'
        startDate = getThisYearStartTime()
      }
      OpenTicketReportApi.statisticsTicketNumber({
        startDate,
        endDate,
        dateType
      }).then((res: any) => {
        const defaultlist = [
          [new Date().getTime() - 1000 * 60 * 10 * 5, 0],
          [new Date().getTime() - 1000 * 60 * 10 * 4, 0],
          [new Date().getTime() - 1000 * 60 * 10 * 3, 0],
          [new Date().getTime() - 1000 * 60 * 10 * 2, 0],
          [new Date().getTime() - 1000 * 60 * 10 * 1, 0]
        ]
        const data = (res && res.length > 0) ? res.map(item => {
          const time = new Date(item.date).getTime()
          return [time, item.number]
        }) : defaultlist

        myEcharts.value && myEcharts.value.setOption({
          series: [
            {
              data: data
            }
          ]
        })
        timer = setTimeout(() => {
          chatRequestCountReport();
        }, 10000);
      })
    }

    function resize () {
      myEcharts.value && myEcharts.value.resize()
    }
    
    onMounted(() => {
      renderChart()
      chatRequestCountReport()
      window.addEventListener('resize', resize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', resize)
    })
    
    return {
      timeRef,
      timeFilter,
      setTime
    }
  }
})
